import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Calendar, ExternalLink } from "lucide-react";

const SERVICES = ["Music Production", "Photography", "Audio Engineering", "Travel Planning", "Project Management", "Creative Consulting"];

export default function Booking() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", service: SERVICES[0], preferred_date: "", details: "" });
  const [submitting, setSubmitting] = useState(false);
  const [calendly, setCalendly] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => {
      if (r.data?.calendar_enabled && r.data?.calendly_url) setCalendly(r.data.calendly_url);
    }).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      toast.success("Booking received. I'll be in touch within 1–2 business days.");
      setForm({ full_name: "", email: "", phone: "", service: SERVICES[0], preferred_date: "", details: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-24 md:py-36 bg-warmwhite border-t border-taupe/30" data-testid="booking-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="text-[11px] uppercase tracking-widest-custom text-olive mb-6">06 — Booking</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight" data-testid="booking-title">
            Reserve a <span className="italic text-gold">consultation.</span>
          </h2>
          <p className="mt-8 text-charcoal/75 leading-relaxed max-w-md">
            {`Tell me about your project and the timeline you're hoping for. Most consultations begin with a 30-minute discovery call to align on vision, scope, and fit.`}
          </p>
          <div className="mt-12 flex items-center gap-4 text-charcoal/70 text-sm">
            <Calendar className="w-5 h-5 text-olive" strokeWidth={1.2} />
            <span>Replies within 1–2 business days</span>
          </div>

          {calendly && (
            <a
              href={calendly}
              target="_blank"
              rel="noreferrer"
              data-testid="booking-calendly-link"
              className="mt-10 inline-flex items-center gap-3 border border-charcoal text-charcoal px-6 py-4 text-[11px] uppercase tracking-widest-custom hover:bg-charcoal hover:text-warmwhite transition-colors duration-300"
            >
              Pick a time directly <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6" data-testid="booking-form">
          <Field label="Full name" value={form.full_name} onChange={update("full_name")} testid="booking-name" required />
          <Field label="Email" type="email" value={form.email} onChange={update("email")} testid="booking-email" required />
          <Field label="Phone (optional)" value={form.phone} onChange={update("phone")} testid="booking-phone" />
          <div className="flex flex-col gap-2">
            <label className="text-[10px] uppercase tracking-widest-custom text-charcoal/55">Service</label>
            <select value={form.service} onChange={update("service")} className="bg-transparent border-b border-taupe focus:border-charcoal outline-none py-2 font-serif text-lg" data-testid="booking-service">
              {SERVICES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <Field label="Preferred date" type="date" value={form.preferred_date} onChange={update("preferred_date")} testid="booking-date" />
          <div className="sm:col-span-2 flex flex-col gap-2">
            <label className="text-[10px] uppercase tracking-widest-custom text-charcoal/55">Tell me about your project</label>
            <textarea rows={4} value={form.details} onChange={update("details")} className="bg-transparent border-b border-taupe focus:border-charcoal outline-none py-2 font-serif text-lg resize-none" data-testid="booking-details" />
          </div>
          <div className="sm:col-span-2 flex justify-end mt-4">
            <button type="submit" disabled={submitting} className="bg-charcoal text-warmwhite px-10 py-5 text-[11px] uppercase tracking-widest-custom hover:bg-gold hover:text-charcoal transition-colors duration-300 disabled:opacity-60" data-testid="booking-submit">
              {submitting ? "Sending…" : "Request consultation"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[10px] uppercase tracking-widest-custom text-charcoal/55">{label}{required && " *"}</label>
      <input type={type} value={value} onChange={onChange} required={required} className="bg-transparent border-b border-taupe focus:border-charcoal outline-none py-2 font-serif text-lg" data-testid={testid} />
    </div>
  );
}
